import { useRef, useCallback, useEffect, useState } from 'react';
import { useGridFilter } from 'ag-grid-react';
import StaticOptions from './staticOptions';

const StaticFilter = (props) => {
  const {
    model,
    onModelChange,
    getValue,
    staticOptions,
    getStaticOptions,
    renderIcon,
    filterFunction,
    context
  } = props;

  const currentModel = model || [];
  const defaultOptionsRef = useRef(getStaticOptions ? getStaticOptions(props) : staticOptions);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(defaultOptionsRef.current);

  const filterOptionsBySearchTerm = useCallback((options, searchValue) => {
    if (!searchValue) {
      return [...options];
    }
    const searchTermLower = searchValue.toLowerCase();
    return options.filter((option) =>
      option.text.toLowerCase().includes(searchTermLower)
    );
  }, []);

  // Update options when props change
  useEffect(() => {
    const newOptions = getStaticOptions ? getStaticOptions(props) : staticOptions;
    defaultOptionsRef.current = newOptions;
    // Apply current search filter to new options
    const filtered = filterOptionsBySearchTerm(newOptions, searchTerm);
    setFilteredOptions(filtered);
  }, [getStaticOptions, staticOptions, props, searchTerm, filterOptionsBySearchTerm]);

  const notifyFilterChanged = useCallback((model) => {
    onModelChange(model.length === 0 ? null : model);
  }, [onModelChange]);

  useEffect(() => {
    // If the model is cleared, reset the filter state (no selected options)
    if (!model || model.length === 0) {
      notifyFilterChanged([]);
    }
  }, [model, notifyFilterChanged]);

  const onSearch = useCallback((value) => {
    const targetValue = value || "";
    setSearchTerm(targetValue);
    const filtered = filterOptionsBySearchTerm(defaultOptionsRef.current, targetValue);
    setFilteredOptions(filtered);
  }, [filterOptionsBySearchTerm]);

  const isOptionSelected = useCallback(
    (value) => {
      if (Array.isArray(value) && value.length > 0) {
        return value.some((item) => currentModel.includes(item));
      }
      return currentModel.includes(value);
    },
    [currentModel]
  );

  const doesFilterPass = useCallback(
    ({ node }) => {
      let currentValue = getValue(node);
      // To be able to filter blank columns
      if (currentValue?.length === 0) {
        currentValue = '';
      }
      if (filterFunction) {
        return filterFunction({
          value: currentValue,
          selectedOptions: currentModel,
          context
        });
      }
      return isOptionSelected(currentValue);
    },
    [getValue, filterFunction, context, currentModel, isOptionSelected]
  );

  const isFilterActive = useCallback(() => {
    return currentModel.length > 0;
  }, [currentModel]);

  const afterGuiAttached = useCallback(() => {
    setSearchTerm('');
    setFilteredOptions([...defaultOptionsRef.current]);
  }, []);

  useGridFilter({
    doesFilterPass,
    isFilterActive,
    afterGuiAttached
  });

  const onOptionSelected = useCallback(
    (value, checked) => {
      const set = new Set(currentModel);
      checked ? set.add(value) : set.delete(value);
      const newModel = [...set];
      notifyFilterChanged(newModel);
    },
    [currentModel, notifyFilterChanged]
  );

  const onSelectAll = useCallback(
    (checked) => {
      const newModel = checked ? filteredOptions.map((option) => option.value) : [];
      notifyFilterChanged(newModel);
    },
    [filteredOptions, notifyFilterChanged]
  );

  return (
    <StaticOptions
      options={filteredOptions}
      renderIcon={renderIcon}
      isOptionSelected={isOptionSelected}
      onOptionSelected={onOptionSelected}
      onSelectAll={onSelectAll}
      onSearch={onSearch}
      searchTerm={searchTerm}
    />
  );
};

export default StaticFilter;
